import React, { useEffect, useRef, useState } from 'react';
import { ColorRing } from 'react-loader-spinner';
import PriceComponent from '../priceComponent';
import { useGetHigherLevelAnalysisMovementHistoryQuery, useGetTestHigherLevelAnalysisMovementHistoryQuery } from '../../redux/slices/apiSlice';
import PriceMovementDataGraph from './PriceMovementDataGraph';
import { useDispatch, useSelector } from 'react-redux';
import { selectSelectedCaratRange, selectSelectedShape, setSelectedCell } from '../../redux/slices/higherOrderSlice';

const PriceMovementData = ({
  defaultTurn,
  priceMovement,
  priceMovementColorKeys,
  priceMovementClarityDatas,
  allpriceMovementClarityRanges,
  isPriceMovementLoading,
  isChangingPricing,
  setIsChangingPricing,
  accountDetails,
}) => {  
  const [hoveredCell, setHoveredCell] = useState(null);
  const drilldownRef = useRef(null);
  const dispatch = useDispatch();
  const caratRanges = useSelector(selectSelectedCaratRange);
  const shaperedux = useSelector(selectSelectedShape);
  
  // Add tracking for previously selected cell
  const [prevSelectedCell, setPrevSelectedCell] = useState(null);
  const [selectedCell, setSelectedTCell] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  
  // Track whether parameters are being changed
  const [isChangingParams, setIsChangingParams] = useState(false);
  
  // Query parameters for fetching drill-down data
  const [queryNParams, setQueryParams] = useState({
    id: accountDetails.account_id,
    shape: shaperedux,
    carat_range: caratRanges,
    color: selectedCell?.color,
    clarity: selectedCell?.clarity,
  });

  // Update shape and carat range when they change in Redux store
  useEffect(() => {
    setQueryParams((prevParams) => ({
      ...prevParams,
      shape: shaperedux,
      carat_range: caratRanges,
    }));
  }, [shaperedux, caratRanges]);
  
  // Fetch drilldown data using the query parameters
  const { data, isLoading, isFetching } = useGetTestHigherLevelAnalysisMovementHistoryQuery(
    queryNParams,
    {
      // Skip the query if we don't have a selected cell yet
      skip: !queryNParams.color || !queryNParams.clarity,
      // Refetch when parameters change
      refetchOnMountOrArgChange: true
    }
  );  

  // Process chart data from API response
  const chartData = data?.data
    ? Object.entries(data.data.history_data).map(([week, values]) => ({
        week, // X-axis
        price_change: values.price_change,
        overhang: values.overhang,
        min_ppc: values.min_ppc,
        max_ppc: values.max_ppc,
        price_change_10th: values.price_change_10th,
        price_change_90th: values.price_change_90th,
      }))
    : [];

  // When a cell is clicked, update parameters and selected cell
  const handleCellClick = (shapeIndex, caratIndex) => {
    const color = priceMovementColorKeys[shapeIndex];
    const clarity = allpriceMovementClarityRanges[caratIndex];
    
    // Track that we're changing parameters for loading state
    setIsChangingParams(true);
    
    // Save the previous selection for comparison
    setPrevSelectedCell(selectedCell);
    
    // Update query parameters with new color and clarity
    setQueryParams(prevParams => ({
      ...prevParams,
      color: color,
      clarity: clarity,
    }));
    
    // Update local selected cell state
    setSelectedTCell({ color, clarity });
    
    // Update Redux store
    dispatch(setSelectedCell({ color, clarity }));

    // Smooth scroll to drilldown component after a brief delay
    setTimeout(() => {
      drilldownRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 100);
  };

  // Track when data changes to update loading state
  useEffect(() => {
    // If we have data and we were changing parameters, reset the flag
    if (data && isChangingParams) {
      setIsChangingParams(false);
    }
    
    // Reset initial load flag once we have data
    if (data && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [data, isChangingParams, isInitialLoad]);

  // Reset loading state when price movement data changes
  useEffect(() => {
    if (priceMovement?.data && isChangingPricing) {
      setIsChangingPricing(false);
    }
  }, [priceMovement, isChangingPricing, setIsChangingPricing]);

  // Handle cell hover for highlighting
  const handleCellHover = (column, row) => {
    setHoveredCell(column !== null && row !== null ? { column, row } : null);
  };

  // Helper function to round floating point values
  const roundIfFloat = (value) => {
    return typeof value === 'number' && value % 1 !== 0
      ? Math.round(value * 100) / 100
      : value;
  };

  // Determine if we should show loader
  const showDrilldownLoader = isLoading || isFetching || isChangingParams;

  return (
    <div className="w-full">
      <br />
      <div className="flex w-full flex-col">
        <div className="w-full border-none flex flex-col justify-between gap-6 -items-center px-8">
          <div className="bg-transparent items-center flex flex-col xxxl:flex-row xxxl:justify-center flex-grow gap-5 w-full">
            <div className="z-0">
              <PriceComponent
                title="Market Overhang"
                data={priceMovement}
                defaultTurn={defaultTurn}
                min={15}
                max={120}
                hoveredCell={hoveredCell}
                onCellHover={handleCellHover}
                priceMovementColorKeys={priceMovementColorKeys}
                allpriceMovementClarityRanges={allpriceMovementClarityRanges}
                priceMovementClarityDatas={priceMovementClarityDatas}
                metric="overhang"
                roundIfFloat={roundIfFloat}
                handleCellClick={handleCellClick}
              />
            </div>
            <div className="z-0">
              <PriceComponent
                title="Market Price Change"
                data={priceMovement}
                defaultTurn={defaultTurn}
                min={-10}
                max={10}
                hoveredCell={hoveredCell}
                onCellHover={handleCellHover}
                priceMovementColorKeys={priceMovementColorKeys}
                allpriceMovementClarityRanges={allpriceMovementClarityRanges}
                priceMovementClarityDatas={priceMovementClarityDatas}
                metric="price_change"
                roundIfFloat={roundIfFloat}
                isChangingShapeOrCarat={isChangingPricing}
                handleCellClick={handleCellClick}
              />
            </div>
          </div>
          <br></br>
          
          {queryNParams.color && queryNParams.clarity && (
            <div className="mt-4">
            <p ref={drilldownRef}></p>
                <PriceMovementDataGraph
                  isLoading={showDrilldownLoader}
                  chartData={chartData}
                  queryNParams={queryNParams}
                  shaperedux={shaperedux}
                  caratRanges={caratRanges} 
                />
              
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PriceMovementData;